import { AvatarGroup } from "@dash/avatar";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { useTheme } from "@emotion/react";
import Decimal from "decimal.js";
import { useLocation } from "react-router-dom-v5-compat";

import { Profile } from "@megaron/auth-contracts";
import { Macroregion } from "@megaron/crm-contracts";

import { RealizationProgressBar } from "./RealizationProgressBar";
import { RegionChips } from "./RegionChips";

export type ExtendedMacroregion = {
  macroregion: Macroregion;
  profiles: Profile[];
  profitMetrics: {
    profit?: Decimal;
    target?: Decimal;
  };
};

type Props = {
  macroregions: ExtendedMacroregion[];
  isLoading: boolean;
};

export const MacroregionsTable: React.FC<Props> = ({ macroregions, isLoading }) => {
  const theme = useTheme();

  const location = useLocation();

  const tableColumns: TableHeadColumn<"">[] = [
    ...tableHeadingsList.map(
      ({ heading }) =>
        ({
          isSortable: false,
          label: heading,
        }) as const,
    ),
  ];

  const getTableRowCellsContent = (macroregion: ExtendedMacroregion): RowCellContent[] => [
    {
      isLink: true,
      cellCss: {
        color: theme.colors.primary,
        fontWeight: 600,
        minWidth: "8rem",
      },
      element: macroregion.macroregion.name,
    },
    {
      isLink: true,
      cellCss: {
        color: theme.colors.primary,
        minWidth: "12rem",
      },
      element: (
        <AvatarGroup
          reverseAvatar
          profiles={
            macroregion.profiles?.map((profile) => ({
              ...profile,
              firstName: profile.firstName ?? null,
              lastName: profile.lastName ?? null,
            })) ?? []
          }
          fullName
        />
      ),
    },
    {
      isLink: true,
      element: macroregion.macroregion.customerCount,
    },
    {
      isLink: true,
      cellCss: { minWidth: "15rem" },
      element:
        macroregion.profitMetrics.profit && macroregion.profitMetrics.target ? (
          <RealizationProgressBar
            currentRealization={macroregion.profitMetrics.profit?.toNumber()}
            goalRealization={macroregion.profitMetrics.target?.toNumber()}
          />
        ) : (
          <span css={{ color: theme.colors.secondaryText, textAlign: "right", marginLeft: "auto" }}>Brak</span>
        ),
    },
    {
      isLink: true,
      cellCss: { minWidth: "12rem" },
      element: <RegionChips regions={macroregion.macroregion.regions} />,
    },
  ];

  return (
    <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
      <Table<""> columns={tableColumns} css={{ padding: "0 2rem" }}>
        <TableBody
          isLoading={isLoading}
          rows={macroregions.map((macroregion) => ({
            uuid: macroregion.macroregion.name,
            cellsContent: getTableRowCellsContent(macroregion),
            linkTo: `/crm/regions/${encodeURIComponent(macroregion.macroregion.name)}${location.search}`,
            css: {
              "&:hover": {
                "td:nth-child(1)": {
                  textDecoration: "underline",
                },
              },
            },
          }))}
        />
      </Table>
    </div>
  );
};

export const tableHeadingsList: { heading: string }[] = [
  { heading: "Nazwa" },
  { heading: "Osoby uprawnione" },
  { heading: "Klienci" },
  { heading: "Realizacja planu marży" },
  { heading: "Regiony" },
];
